/* Kasa · Bloque guiado "Crea tu Explorador"
 * ------------------------------------------
 * Pinta 4 pasos numerados y CONDUCE lo nativo (no lo reimplementa):
 *   1. Tamaño     -> clica los radios de variante nativos (Horizon actualiza precio)
 *   2. ¿Uno o dos?-> property "Modo"
 *   3. Café + molienda -> "Café 1/2" y "Molienda 1/2" en el <form> nativo
 *   4. Frecuencia -> conduce el <select name="subs_interval"> de Seal (Seal escribe selling_plan)
 * El botón/cantidad nativos siguen abriendo el cajón lateral. Bloquea el add si faltan cafés.
 */

(function () {
  'use strict';

  class KasaExploradorCoffeeSelect extends HTMLElement {
    connectedCallback() {
      // El bloque suele ir ANTES del formulario en el DOM, así que el form
      // puede no existir todavía cuando el elemento se actualiza. Esperamos a
      // que aparezca en vez de abortar (esa carrera dejaba el bloque muerto).
      this.#whenFormReady((form) => this.#init(form));

      // En el editor de temas, guardar o seleccionar re-renderiza la sección:
      // el contenido del bloque se reemplaza y nuestros botones desaparecen.
      // Reinicializamos en esos eventos.
      if (!this.editorWired && window.Shopify && window.Shopify.designMode) {
        this.editorWired = true;
        ['shopify:section:load', 'shopify:block:select', 'shopify:section:select'].forEach((evt) => {
          document.addEventListener(evt, () => {
            if (!this.isConnected) return;
            this.initialized = false;
            this.#whenFormReady((form) => this.#init(form));
          });
        });
      }
    }

    disconnectedCallback() {
      // Permite re-inicializar si el elemento se reinserta (re-render del editor)
      this.initialized = false;
    }

    #whenFormReady(cb) {
      const tryNow = () => {
        const form = this.#findForm();
        if (form) { cb(form); return true; }
        return false;
      };
      if (tryNow()) return;
      const obs = new MutationObserver(() => { if (tryNow()) obs.disconnect(); });
      obs.observe(document.documentElement, { childList: true, subtree: true });
      // Red de seguridad por si el form llega tras el parseo inicial
      document.addEventListener('DOMContentLoaded', tryNow, { once: true });
      window.addEventListener('load', () => { if (tryNow()) obs.disconnect(); }, { once: true });
    }

    #init(form) {
      if (this.initialized) return;
      this.initialized = true;

      this.duoMap = this.#safeJSON(this.getAttribute('data-duo-map')) || {};
      // Cafés que no se ofrecen con "un café" en un tamaño: "500g: Perla Blanca; 1kg: X"
      this.monoExclude = {};
      (this.getAttribute('data-mono-exclude') || '').split(/[;\n]/).forEach((rule) => {
        const [size, names] = rule.split(':');
        if (!size || !names) return;
        this.monoExclude[size.toLowerCase().replace(/\s+/g, '')] =
          names.split(',').map((n) => n.trim().toLowerCase()).filter(Boolean);
      });
      this.variants = this.#safeJSON(this.getAttribute('data-variants')) || [];
      // Candidatas a muestra, en el orden de la colección. Ver #sampleFor().
      this.samples = this.#safeJSON(this.getAttribute('data-samples')) || [];
      this.grindOptions = (this.getAttribute('data-grind-options') || '')
        .split(',').map((s) => s.trim()).filter(Boolean);
      this.grindLabel = this.getAttribute('data-grind-label') || 'Molienda';
      this.sealHideSelector = this.getAttribute('data-seal-hide') || '';
      // Respaldo cuando el widget de Seal no aparece; ver #renderFreqNativo()
      this.sellingPlans = this.#safeJSON(this.getAttribute('data-selling-plans')) || [];
      // Etiquetas visibles del carrito, traducidas. Las claves con "_" no.
      this.props_ = {
        coffee: this.getAttribute('data-prop-coffee') || 'Café',
        coffee1: this.getAttribute('data-prop-coffee-1') || 'Café 1',
        coffee2: this.getAttribute('data-prop-coffee-2') || 'Café 2',
        sample: this.getAttribute('data-prop-sample') || 'Muestra',
      };

      // Textos traducidos, inyectados desde Liquid (locales/*.json)
      const t = (name, fallback) => this.getAttribute('data-i18n-' + name) || fallback;
      this.i18n = {
        chooseOne: t('choose-one', 'elige 1'),
        chooseTwo: t('choose-two', 'elige 2'),
        needOne: t('need-one', 'Elige un café para continuar.'),
        needTwo: t('need-two', 'Elige dos cafés para continuar.'),
        needBeforeAdd: t('need-before-add', 'Elige tu café antes de añadir.'),
        splitNote: t('split-note', ''),
        grindHelp: t('grind-help', ''),
        modeMono: t('mode-mono', 'Un café'),
        modeDuo: t('mode-duo', 'Dos cafés'),
      };

      this.form = form;
      this.variantInput = this.form.querySelector('input[name="id"]');

      // Refs
      this.steps = Array.from(this.querySelectorAll('[data-kxc-step]'));
      this.sizeStep = this.querySelector('[data-kxc-size-step]');
      this.sizeOpts = this.querySelector('[data-kxc-size-opts]');
      this.modeStep = this.querySelector('[data-kxc-mode-step]');
      this.modeInputs = Array.from(this.querySelectorAll('[data-kxc-mode]'));
      this.coffeeInputs = Array.from(this.querySelectorAll('[data-kxc-coffee-input]'));
      this.counter = this.querySelector('[data-kxc-counter]');
      this.summary = this.querySelector('[data-kxc-summary]');
      this.freqStep = this.querySelector('[data-kxc-freq-step]');
      this.freqOpts = this.querySelector('[data-kxc-freq-opts]');
      this.hint = this.querySelector('[data-kxc-hint]');
      this.modeSubs = Array.from(this.querySelectorAll('[data-kxc-mode-sub]'));
      this.modeNote = this.querySelector('[data-kxc-mode-note]');

      this.selection = [];
      this.grinds = {};
      // Los hidden inputs viven en el form (que sobrevive al re-render del
      // bloque), así que reutilizamos los que ya existan en vez de duplicarlos.
      this.props = new Map();
      this.form.querySelectorAll('input[data-kxc-prop]').forEach((input) => {
        this.props.set(input.getAttribute('data-kxc-prop'), input);
      });

      // Init idempotente: limpiamos lo generado antes de repintar
      if (this.sizeOpts) this.sizeOpts.innerHTML = '';
      if (this.freqOpts) this.freqOpts.innerHTML = '';
      if (this.summary) { this.summary.innerHTML = ''; this.summary.hidden = true; }

      this.#disableSingleGrind();
      this.#buildSizeStep();
      this.#buildFreqStep();
      this.#updateModeLabels();

      this.modeInputs.forEach((el) => el.addEventListener('change', () => {
        const label = el.closest('.kxc__opt');
        if (label) this.#setFillDirection('mode', label);
        this.#applyMode();
      }));
      this.coffeeInputs.forEach((el) => el.addEventListener('change', (e) => this.#onCoffeeToggle(e.target)));
      this.#watchVariant();
      this.#wireSubmitGuard();

      this.#onVariantChange();

      // Punto de partida de cada grupo, para orientar el primer cambio.
      // Se reinicia siempre: tras un re-render del editor las referencias
      // anteriores apuntan a nodos ya sustituidos.
      this.selected = {};
      this.selected.size = this.sizeOpts && this.sizeOpts.querySelector('[aria-pressed="true"]');
      this.selected.freq = this.freqOpts && this.freqOpts.querySelector('[aria-pressed="true"]');
      const checkedMode = this.modeInputs.find((el) => el.checked);
      this.selected.mode = checkedMode ? checkedMode.closest('.kxc__opt') : null;
    }

    // ---------- utilidades ----------
    #safeJSON(str) { try { return JSON.parse(str); } catch (_) { return null; } }

    // Orienta el relleno según hacia dónde se mueve la selección dentro del
    // grupo: el saliente colapsa hacia el lado por el que "sale" el color y el
    // entrante crece desde el lado por el que "entra", así ambos bordes viajan
    // en el mismo sentido. Con opciones en varias filas usamos la posición
    // real en pantalla, no el orden en el DOM.
    #setFillDirection(key, newEl) {
      const oldEl = this.selected && this.selected[key];
      if (!this.selected) this.selected = {};
      this.selected[key] = newEl;
      if (!oldEl || !newEl || oldEl === newEl) return;

      const a = oldEl.getBoundingClientRect();
      const b = newEl.getBoundingClientRect();
      // Si están en filas distintas, la referencia es la vertical
      const sameRow = Math.abs(a.top - b.top) < 4;
      const forward = sameRow ? b.left > a.left : b.top > a.top;

      oldEl.style.setProperty('--kxc-origin', forward ? 'right' : 'left');
      newEl.style.setProperty('--kxc-origin', forward ? 'left' : 'right');
    }

    #findForm() {
      const forms = Array.from(document.querySelectorAll('form[data-type="add-to-cart-form"]'));
      return forms.find((f) => !f.closest('#quick-add-dialog')) || forms[0] || null;
    }

    #disableSingleGrind() {
      const single = this.form.querySelector('[data-single-grind-field]');
      if (!single) return;
      single.hidden = true;
      single.querySelectorAll('select, input').forEach((el) => (el.disabled = true));
    }

    // ---------- PASO 1 · Tamaño ----------
    // Dos modos: si existe el variant-picker nativo, lo conducimos (Horizon
    // actualiza el precio). Si no está en la plantilla, pintamos las variantes
    // desde Liquid y fijamos input[name="id"] directamente.
    #buildSizeStep() {
      if (!this.sizeOpts) return;

      this.sizeRadios = Array.from(document.querySelectorAll('variant-picker input[type="radio"]'))
        .filter((r) => !r.closest('#quick-add-dialog'));

      if (this.sizeRadios.length) {
        this.#buildSizeFromRadios();
      } else if (this.variants.length > 1) {
        this.#buildSizeFromVariants();
      } else {
        return; // una sola variante: no hay nada que elegir
      }

      this.sizeStep.hidden = false;
      this.#reflectSize();
      this.#renumber();
    }

    #buildSizeFromRadios() {
      this.sizeMode = 'radios';
      this.sizeRadios.forEach((radio) => {
        const btn = this.#sizeButton(this.#radioLabel(radio));
        btn.addEventListener('click', () => {
          this.#setFillDirection('size', btn);
          radio.click(); // Horizon actualiza variante y precio
        });
        radio._kxcBtn = btn;
        this.sizeOpts.appendChild(btn);
      });
      document.querySelectorAll('variant-picker').forEach((vp) => {
        if (!vp.closest('#quick-add-dialog')) vp.style.display = 'none';
      });
      this.form.addEventListener('change', () => this.#reflectSize());
    }

    #buildSizeFromVariants() {
      this.sizeMode = 'variants';
      this.variants.forEach((v) => {
        const btn = this.#sizeButton(v.title, v.price);
        if (!v.available) { btn.disabled = true; btn.style.opacity = '.4'; }
        btn.dataset.variantId = String(v.id);
        btn.addEventListener('click', () => {
          if (!this.variantInput) return;
          this.#setFillDirection('size', btn);
          this.variantInput.value = String(v.id);
          this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          this.#reflectSize();
          this.#onVariantChange();
        });
        this.sizeOpts.appendChild(btn);
      });
    }

    #sizeButton(labelText, subText) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'kxc__opt';
      btn.setAttribute('data-kxc-size-btn', '');
      const label = document.createElement('span');
      label.className = 'kxc__opt-label';
      label.textContent = labelText;
      btn.appendChild(label);
      if (subText) {
        const sub = document.createElement('span');
        sub.className = 'kxc__opt-sub';
        sub.textContent = subText;
        btn.appendChild(sub);
      }
      return btn;
    }

    #radioLabel(radio) {
      const forLbl = radio.id && document.querySelector('label[for="' + radio.id + '"]');
      const lbl = forLbl || radio.closest('label');
      const txt = (lbl ? lbl.textContent : radio.value) || '';
      return txt.replace(/\s+/g, ' ').trim();
    }

    #reflectSize() {
      if (this.sizeMode === 'radios') {
        this.sizeRadios.forEach((r) => {
          if (r._kxcBtn) r._kxcBtn.setAttribute('aria-pressed', r.checked ? 'true' : 'false');
        });
        return;
      }
      const current = this.variantInput ? String(this.variantInput.value) : '';
      this.sizeOpts.querySelectorAll('[data-variant-id]').forEach((btn) => {
        btn.setAttribute('aria-pressed', btn.dataset.variantId === current ? 'true' : 'false');
      });
    }

    // ---------- PASO 4 · Frecuencia (proxy del select de Seal) ----------
    #buildFreqStep() {
      const select = this.#findSealSelect();
      if (!select) {
        // Seal puede renderizar tarde: se espera un poco por si aparece, pero
        // no indefinidamente. En los idiomas que no son el principal no llega
        // nunca, y sin frecuencia no se puede añadir al carrito: pasado ese
        // plazo se construye el paso con los planes que da Liquid.
        let resuelto = false;
        const obs = new MutationObserver(() => {
          const s = this.#findSealSelect();
          if (s) { resuelto = true; obs.disconnect(); this.#renderFreq(s); }
        });
        obs.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => {
          if (resuelto) return;
          obs.disconnect();
          this.#renderFreqNativo();
        }, 2500);
        return;
      }
      this.#renderFreq(select);
    }

    /**
     * Paso de frecuencia sin Seal, escribiendo `selling_plan` a mano.
     *
     * Es lo único que el carrito necesita: Shopify asocia el plan al pedido y
     * Seal crea el contrato a partir de ahí. Los ids y los nombres vienen
     * traducidos desde Liquid, así que esto funciona en cualquier idioma.
     */
    #renderFreqNativo() {
      if (!this.freqOpts || !this.sellingPlans.length) return;

      let input = this.form.querySelector('input[name="selling_plan"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        this.form.appendChild(input);
      }

      this.freqOpts.innerHTML = '';
      this.sellingPlans.forEach((plan, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'kxc__opt';
        const label = document.createElement('span');
        label.className = 'kxc__opt-label';
        label.textContent = plan.name;
        btn.appendChild(label);
        btn.addEventListener('click', () => {
          this.#setFillDirection('freq', btn);
          input.value = String(plan.id);
          this.freqOpts.querySelectorAll('.kxc__opt').forEach((b) =>
            b.setAttribute('aria-pressed', String(b === btn))
          );
        });
        this.freqOpts.appendChild(btn);
        // Sin una frecuencia elegida no se puede comprar: se parte de la primera
        if (i === 0) {
          input.value = String(plan.id);
          btn.setAttribute('aria-pressed', 'true');
        }
      });

      if (this.freqStep) this.freqStep.hidden = false;
      this.#renumber();
    }

    #findSealSelect() {
      return Array.from(document.querySelectorAll('select[name="subs_interval"]'))
        .find((s) => !s.closest('#quick-add-dialog')) || null;
    }

    #renderFreq(select) {
      if (!this.freqOpts) return;
      this.freqOpts.innerHTML = '';
      Array.from(select.options).forEach((opt, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'kxc__opt';
        const label = document.createElement('span');
        label.className = 'kxc__opt-label';
        label.textContent = opt.textContent.replace(/\s+/g, ' ').trim();
        btn.appendChild(label);
        btn.addEventListener('click', () => {
          this.#setFillDirection('freq', btn);
          select.selectedIndex = i;
          select.dispatchEvent(new Event('change', { bubbles: true })); // Seal escribe selling_plan
          this.#reflectFreq(select);
        });
        opt._kxcBtn = btn;
        this.freqOpts.appendChild(btn);
      });

      // Ocultar el widget de Seal (el select sigue funcionando por debajo)
      let target = null;
      if (this.sealHideSelector) target = document.querySelector(this.sealHideSelector);
      if (!target) target = select.closest('.sealsubs-container') || select.closest('.seal-row') || select.closest('div');
      if (target) target.style.display = 'none';

      select.addEventListener('change', () => this.#reflectFreq(select));
      this.freqStep.hidden = false;
      this.#reflectFreq(select);
      this.#renumber();
    }

    #reflectFreq(select) {
      Array.from(select.options).forEach((opt) => {
        if (opt._kxcBtn) opt._kxcBtn.setAttribute('aria-pressed', opt.selected ? 'true' : 'false');
      });
    }

    // ---------- Variante -> ¿permite dúo? ----------
    #watchVariant() {
      if (!this.variantInput) return;
      new MutationObserver(() => this.#onVariantChange())
        .observe(this.variantInput, { attributes: true, attributeFilter: ['value'] });
      document.addEventListener('variant:update', () => this.#onVariantChange());
      this.form.addEventListener('change', (e) => {
        if (e.target && e.target.name === 'id') this.#onVariantChange();
      });
    }

    get supportsDuo() {
      const id = this.variantInput ? String(this.variantInput.value) : '';
      return this.duoMap[id] === true;
    }
    get isDuo() {
      if (!this.supportsDuo) return false;
      const checked = this.modeInputs.find((el) => el.checked);
      return checked ? checked.value === 'duo' : false;
    }
    get requiredCount() { return this.isDuo ? 2 : 1; }
    get excludedNow() {
      if (this.isDuo) return [];
      const v = this.#currentVariant();
      return (v && this.monoExclude[String(v.title).toLowerCase().replace(/\s+/g, '')]) || [];
    }

    #onVariantChange() {
      const duo = this.supportsDuo;
      if (this.modeStep) this.modeStep.hidden = !duo;
      if (!duo) {
        const mono = this.modeInputs.find((el) => el.value === 'mono');
        if (mono) mono.checked = true;
      }
      this.#updateModeLabels();
      this.#applyMode();
      this.#renumber();
    }

    // ---------- Desglose de cantidad (mono vs dúo) ----------
    // El dúo REPARTE el mismo peso, no lo duplica. Lo hacemos explícito:
    // 1kg -> "1 × 1kg" frente a "2 × 500g = 1kg".
    #currentVariant() {
      const id = this.variantInput ? String(this.variantInput.value) : '';
      return this.variants.find((v) => String(v.id) === id) || null;
    }

    #parseGrams(title) {
      const m = String(title || '').match(/([\d]+(?:[.,][\d]+)?)\s*(kg|g)\b/i);
      if (!m) return null;
      const n = parseFloat(m[1].replace(',', '.'));
      if (!isFinite(n)) return null;
      return m[2].toLowerCase() === 'kg' ? Math.round(n * 1000) : Math.round(n);
    }

    #formatGrams(g) {
      if (g >= 1000 && g % 1000 === 0) return (g / 1000) + 'kg';
      return g + 'g';
    }

    #updateModeLabels() {
      if (!this.modeSubs || !this.modeSubs.length) return;
      const variant = this.#currentVariant();
      const total = variant ? this.#parseGrams(variant.title) : null;

      if (!total) { // sin peso reconocible: no inventamos cifras
        this.modeSubs.forEach((el) => (el.textContent = ''));
        if (this.modeNote) this.modeNote.textContent = '';
        return;
      }

      const half = this.#formatGrams(total / 2);
      const whole = this.#formatGrams(total);
      const texts = ['1 × ' + whole, '2 × ' + half + ' = ' + whole];
      this.modeSubs.forEach((el, i) => (el.textContent = texts[i] || ''));

      if (this.modeNote) {
        this.modeNote.textContent = (this.i18n.splitNote || '')
          .replace('{{ total }}', whole)
          .replace('{{ half }}', half);
      }
    }

    #applyMode() {
      const max = this.requiredCount;
      if (this.selection.length > max) {
        this.selection.slice(max).forEach((val) => {
          const input = this.coffeeInputs.find((el) => el.value === val);
          if (input) input.checked = false;
          delete this.grinds[val];
        });
        this.selection = this.selection.slice(0, max);
      }
      // Si el café elegido no se ofrece en este tamaño, se deselecciona
      const excluded = this.excludedNow;
      this.selection = this.selection.filter((val) => {
        if (!excluded.includes(val.toLowerCase())) return true;
        const input = this.coffeeInputs.find((el) => el.value === val);
        if (input) input.checked = false;
        delete this.grinds[val];
        return false;
      });
      this.#render();
    }

    #onCoffeeToggle(input) {
      const max = this.requiredCount;
      if (input.checked) {
        if (!this.selection.includes(input.value)) this.selection.push(input.value);
        while (this.selection.length > max) {
          const evicted = this.selection.shift();
          const el = this.coffeeInputs.find((c) => c.value === evicted);
          if (el) el.checked = false;
          delete this.grinds[evicted];
        }
        if (!this.grinds[input.value]) this.grinds[input.value] = this.grindOptions[0] || '';
      } else {
        this.selection = this.selection.filter((v) => v !== input.value);
        delete this.grinds[input.value];
      }
      this.#render();
    }

    // ---------- Render ----------
    #render() {
      const max = this.requiredCount;
      const excluded = this.excludedNow;
      this.coffeeInputs.forEach((el) => {
        const card = el.closest('[data-kxc-coffee]');
        if (!card) return;
        const off = excluded.includes(el.value.toLowerCase());
        card.style.display = off ? 'none' : '';
        el.disabled = off;
        const idx = this.selection.indexOf(el.value);
        const orderEl = card.querySelector('[data-kxc-order]');
        if (idx > -1 && this.isDuo) {
          card.setAttribute('data-kxc-show-order', '');
          if (orderEl) orderEl.textContent = String(idx + 1);
        } else {
          card.removeAttribute('data-kxc-show-order');
          if (orderEl) orderEl.textContent = '';
        }
        const full = this.selection.length >= max;
        if (full && idx === -1) card.setAttribute('data-kxc-disabled', '');
        else card.removeAttribute('data-kxc-disabled');
      });

      this.#renderSummary();
      this.#writeProperties();

      if (this.counter) {
        this.counter.textContent = max === 1
          ? '· ' + this.i18n.chooseOne
          : '· ' + this.i18n.chooseTwo + ' (' + this.selection.length + '/2)';
      }
      this.#validate();
    }

    #renderSummary() {
      if (!this.summary) return;
      if (this.selection.length === 0) { this.summary.hidden = true; this.summary.innerHTML = ''; return; }
      this.summary.hidden = false;
      this.summary.innerHTML = '';
      this.selection.forEach((title, i) => {
        const row = document.createElement('div'); row.className = 'kxc__row';
        const badge = document.createElement('span'); badge.className = 'kxc__row-badge';
        badge.textContent = this.isDuo ? String(i + 1) : ''; // en mono no hay orden que numerar
        const main = document.createElement('div'); main.className = 'kxc__row-main';
        const head = document.createElement('div'); head.className = 'kxc__row-head';
        const name = document.createElement('span'); name.className = 'kxc__row-name'; name.textContent = title;
        if (badge.textContent) head.appendChild(badge); // el número vive junto al nombre
        head.appendChild(name);
        if (this.i18n.grindHelp) {
          const help = document.createElement('span');
          help.className = 'kxc__row-help';
          help.textContent = this.i18n.grindHelp;
          head.appendChild(help);
        }
        const select = document.createElement('select'); select.className = 'kxc__grind';
        select.setAttribute('aria-label', this.grindLabel + ' · ' + title);
        this.grindOptions.forEach((opt) => {
          const o = document.createElement('option'); o.value = opt; o.textContent = opt;
          if (opt === this.grinds[title]) o.selected = true;
          select.appendChild(o);
        });
        select.addEventListener('change', () => { this.grinds[title] = select.value; this.#writeProperties(); });
        main.appendChild(head); main.appendChild(select);
        row.appendChild(main);
        this.summary.appendChild(row);
      });
    }

    // Carrito y pedido: una línea por café, completa y lista para empaquetar
    // ("Persica · 250g · Prensa francesa") en vez de cinco líneas sueltas. Los
    // datos estructurados van aparte con prefijo "_": Shopify los guarda en el
    // pedido pero no los muestra al cliente, así la operativa sigue teniendo
    // campos limpios que parsear.
    #writeProperties() {
      const duo = this.isDuo;
      const variant = this.#currentVariant();
      const total = variant ? this.#parseGrams(variant.title) : null;
      // El peso por bolsa solo aporta información en dúo; en mono ya lo dice la variante
      const bag = duo && total ? this.#formatGrams(total / 2) : null;
      const line = (title) => [title, bag, this.grinds[title]].filter(Boolean).join(' · ');

      const one = this.selection[0];
      const two = this.selection[1];

      // Visibles, en el idioma del cliente
      this.#setProp(this.props_.coffee, !duo && one ? line(one) : '', !duo && !!one);
      this.#setProp(this.props_.coffee1, duo && one ? line(one) : '', duo && !!one);
      this.#setProp(this.props_.coffee2, duo && two ? line(two) : '', duo && !!two);

      // Ocultas (operativa)
      this.#setProp('_cafe_1', one || '', !!one);
      this.#setProp('_molienda_1', (one && this.grinds[one]) || '', !!one);
      this.#setProp('_cafe_2', duo && two ? two : '', duo && !!two);
      this.#setProp('_molienda_2', duo && two ? this.grinds[two] || '' : '', duo && !!two);
      this.#setProp('_modo', duo ? 'duo' : 'mono', true);
      this.#setProp('_bolsa', bag || (total ? this.#formatGrams(total) : ''), !!total);

      // La muestra del PRIMER pedido. Los siguientes los estampa el webhook al
      // cobrar, pero este pedido nace de un checkout: cuando Seal crea el
      // contrato ya está emitido, así que si no va aquí, la primera caja sale
      // sin muestra —justo lo que promete la página del Explorador.
      const sample = this.#sampleFor([one, two].filter(Boolean));
      this.#setProp(this.props_.sample, sample || '', !!sample); // visible: la ve quien empaqueta
      this.#setProp('_muestra', sample || '', !!sample);
    }

    /**
     * La primera muestra de la colección que el cliente no lleva ya.
     *
     * Misma regla que `sample-webhook.js` a propósito: si las dos difieren, el
     * primer envío no encaja con los siguientes.
     */
    #sampleFor(chosen) {
      const lleva = chosen.map((t) => String(t).toLowerCase());
      return this.samples.find((t) => !lleva.includes(String(t).toLowerCase())) || null;
    }

    #setProp(label, value, enabled) {
      let input = this.props.get(label);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + label + ']';
        input.setAttribute('data-kxc-prop', label); // permite recuperarlo tras re-render
        this.form.appendChild(input);
        this.props.set(label, input);
      }
      input.value = value;
      input.disabled = !enabled;
    }

    // ---------- Numeración dinámica de pasos visibles ----------
    #renumber() {
      let n = 0;
      this.steps.forEach((step) => {
        const num = step.querySelector('.kxc__num');
        if (!num) return;
        if (step.hidden) { num.textContent = ''; return; }
        n += 1;
        num.textContent = String(n);
      });
    }

    // ---------- Validación / bloqueo ----------
    #validate() {
      const ok = this.selection.length >= this.requiredCount;
      if (this.hint) {
        this.hint.textContent = ok ? '' : (this.requiredCount === 2 ? this.i18n.needTwo : this.i18n.needOne);
      }
      this.valid = ok;
      this.#reflectValidity();
    }

    /**
     * Bloquea la compra mientras no haya café elegido.
     *
     * El guardia del `submit` no basta: los botones de pago acelerado (Shop
     * Pay, Apple Pay, Google Pay) los pinta Shopify y van directos al
     * checkout sin pasar por el formulario. Alguien podía suscribirse de un
     * toque, sin café y sin ninguna de las properties, y eso llega a
     * producción como una suscripción que nadie sabe qué contiene.
     *
     * Por eso se marca el formulario entero: el CSS apaga el pago acelerado y
     * aquí se deshabilita el botón de añadir, que además comunica mejor que
     * dejar pulsar y luego negarse.
     */
    #reflectValidity() {
      if (!this.form) return;
      this.form.toggleAttribute('data-kxc-invalid', !this.valid);

      const addBtn = this.form.querySelector('[type="submit"]');
      if (addBtn) {
        addBtn.disabled = !this.valid;
        addBtn.setAttribute('aria-disabled', String(!this.valid));
      }
    }

    #wireSubmitGuard() {
      this.form.addEventListener('submit', (e) => {
        if (this.valid) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (this.hint) this.hint.textContent = this.i18n.needBeforeAdd;
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, true);
    }
  }

  if (!customElements.get('kasa-explorador-coffee-select')) {
    customElements.define('kasa-explorador-coffee-select', KasaExploradorCoffeeSelect);
  }
})();
